"use client";

import { COOKIE_POLICY_NAME, IS_PRODUCTION } from "@frontend/roz-sdk-core";
import { UiButton, UiContainer, UiText } from "@frontend/roz-uikit";
import Cookies from "js-cookie";
import { type FC, type MouseEvent, useEffect, useState } from "react";

import { cn } from "@/lib/cn";

export interface CookieBannerProps {
  enabled?: boolean;
}

export const CookieBanner: FC<CookieBannerProps> = ({ enabled = true }) => {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(
    () => !!Cookies.get(COOKIE_POLICY_NAME)
  );
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

  const handleMoreInfoClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsMoreInfoOpen(!isMoreInfoOpen);
  };

  const handleCookieAccepted = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    Cookies.set(COOKIE_POLICY_NAME, "true", {
      expires: 365,
      secure: true,
      domain: IS_PRODUCTION
        ? "znanierussia.ru"
        : globalThis.window.location.hostname,
    });
    setIsPolicyAccepted(() => !!Cookies.get(COOKIE_POLICY_NAME));
  };

  useEffect(() => {
    const handleDocumentActivity = () => {
      if (!bannerVisible) {
        setBannerVisible(true);

        document.removeEventListener("mousemove", handleDocumentActivity);
        document.removeEventListener("click", handleDocumentActivity);
        document.removeEventListener("touchmove", handleDocumentActivity);
      }
    };

    if (document && !isPolicyAccepted) {
      document.addEventListener("mousemove", handleDocumentActivity);
      document.addEventListener("click", handleDocumentActivity);
      document.addEventListener("touchmove", handleDocumentActivity);
    }

    return () => {
      document.removeEventListener("mousemove", handleDocumentActivity);
      document.removeEventListener("click", handleDocumentActivity);
      document.removeEventListener("touchmove", handleDocumentActivity);
    };
  }, [bannerVisible, isPolicyAccepted]);

  if (!enabled || isPolicyAccepted || !bannerVisible) {
    return null;
  }

  return (
    <div className="sdkui:pointer-events-auto sdkui:fixed sdkui:bottom-5 sdkui:left-0 sdkui:z-999 sdkui:w-full sdkui:px-4">
      <UiContainer className="sdkui:rounded-xl sdkui:bg-dark-90 sdkui:p-6 sdkui:text-white">
        <div className="sdkui:items-center sdkui:justify-stretch sdkui:gap-10 sdkui:md:flex">
          <div className="sdkui:mb-5 sdkui:h-full sdkui:md:mb-0">
            <UiText className="sdkui:mb-4" color="white" weight="bold">
              🍪 Российское общество «Знание» использует COOKIE
            </UiText>
            <UiText color="white">
              Продолжая использовать наш сайт, Вы даете согласие на обработку
              файлов cookie, пользовательских данных, в целях эффективной работы
              сайта, проведения ретаргетинга и статистических исследований. Вы
              можете запретить обработку Cookies в настройках браузера.
            </UiText>
            <button
              className="sdkui:cursor-pointer sdkui:text-primary-60"
              onClick={handleMoreInfoClick}
              type="button"
            >
              {isMoreInfoOpen ? "Скрыть" : "Подробнее"}
            </button>
            <UiText
              className={cn(
                "sdkui:mt-4 sdkui:hidden",
                isMoreInfoOpen && "sdkui:block"
              )}
              color="white"
            >
              Пользовательские данные: тип и версия Браузера; сведения о
              местоположении; тип и версия ОС; тип устройства и разрешение его
              экрана; источник откуда пришел на сайт пользователь; какие
              страницы открывает и на какие кнопки нажимает пользователь;
              ip-адрес.
            </UiText>
          </div>
          <div className="sdkui:min-w-52 sdkui:flex-0 sdkui:md:flex">
            <UiButton
              className="sdkui:w-full sdkui:outline sdkui:hover:outline-white"
              data-testid="Agree-cooke"
              onClick={handleCookieAccepted}
              variant="white"
            >
              Согласен
            </UiButton>
          </div>
        </div>
      </UiContainer>
    </div>
  );
};
